import { useState, useEffect } from 'react';
import { Event } from './useEvents';

export const useEventFilters = (events: Event[]) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showUpcomingOnly, setShowUpcomingOnly] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 6;

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showUpcomingOnly]);

  // Helper function to check if an event is upcoming
  const isEventUpcoming = (eventDate: string): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const [year, month, day] = eventDate.split('-');
    const event = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return event >= today;
  };

  // Function to generate alternative date formats for an event date
  const generateDateFormats = (dateString: string): string[] => {
    if (!dateString) return [];
    
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    const monthNames = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ];
    
    const monthShortNames = [
      'jan', 'feb', 'mar', 'apr', 'may', 'jun',
      'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
    ];
    
    const monthIndex = parseInt(month) - 1;
    const monthName = monthNames[monthIndex];
    const monthShortName = monthShortNames[monthIndex];
    
    return [
      // Standard format from formatDateForDisplay (e.g., "Jun 15, 2025")
      formatDateForDisplay(dateString).toLowerCase(),
      
      // Numeric formats
      `${day}/${month}/${year}`,
      `${day}-${month}-${year}`,
      `${day}/${month.padStart(2, '0')}/${year}`,
      `${day}-${month.padStart(2, '0')}-${year}`,
      
      // Month name formats
      `${day} ${monthName} ${year}`,
      `${day} ${monthShortName} ${year}`,
      `${monthName} ${day} ${year}`,
      `${monthShortName} ${day} ${year}`,
      `${day} ${monthName}`,
      `${day} ${monthShortName}`,
      
      // Month name formats with day first
      `${parseInt(day)} ${monthName} ${year}`,
      `${parseInt(day)} ${monthShortName} ${year}`,
      `${monthName} ${parseInt(day)} ${year}`,
      `${monthShortName} ${parseInt(day)} ${year}`,
      `${parseInt(day)} ${monthName}`,
      `${parseInt(day)} ${monthShortName}`,
      
      // Just month and year
      `${monthName} ${year}`,
      `${monthShortName} ${year}`,
      
      // Just day and month
      `${day} ${month}`,
      `${month} ${day}`,
      
      // Just numbers
      day,
      month,
      year
    ];
  };

  // Helper function to format date for display
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    // Parse as local date to avoid timezone conversion
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter events based on search term and upcoming/past filter
  const filteredEvents = events.filter(event => {
    // Search filter - check event name, description, date, and track image title
    const searchTermLower = searchTerm.trim().toLowerCase();
    
    // If no search term, just apply date filter
    if (searchTermLower === '') {
      const matchesDateFilter = showUpcomingOnly ? isEventUpcoming(event.event_date) : true;
      return matchesDateFilter;
    }
    
    // Generate all possible date formats for this event
    const dateFormats = generateDateFormats(event.event_date);
    
    // Check if search term matches any of the event properties or date formats
    const matchesSearch = 
      event.name.toLowerCase().includes(searchTermLower) ||
      event.description.toLowerCase().includes(searchTermLower) ||
      (event.track_image?.title && event.track_image.title.toLowerCase().includes(searchTermLower)) ||
      dateFormats.some(format => format.includes(searchTermLower));
    
    // If searching, don't apply the upcoming filter
    if (searchTermLower !== '') {
      return matchesSearch;
    }
    
    // Otherwise apply the upcoming filter if enabled
    const matchesDateFilter = showUpcomingOnly ? isEventUpcoming(event.event_date) : true;
    return matchesSearch && matchesDateFilter;
  });

  // Calculate pagination
  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / eventsPerPage));
  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = filteredEvents.slice(indexOfFirstEvent, indexOfLastEvent);

  return {
    searchTerm,
    setSearchTerm,
    showUpcomingOnly,
    setShowUpcomingOnly,
    currentPage,
    setCurrentPage,
    eventsPerPage,
    filteredEvents,
    currentEvents,
    totalPages,
    isEventUpcoming,
    formatDateForDisplay
  };
};